import { useEffect, useRef, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { text, type Locale } from '../shared/content';
import { api, ApiError } from './api';
import { errorText } from './i18n';
import type { AdPipelineReport, AdStageCounts } from '../server/ad-reports';

type Counts = { impressions: number; clicks: number; uniqueReach: number; uniqueClickers: number };
type AdReportDay = { day: string; impressions: number; clicks: number };
export type AdReport = {
  from: string;
  to: string;
  timezone: 'UTC';
  dayCount: number;
  measurement: 'daily_deduplicated_internal_estimate';
  campaigns: (Counts & { campaignId: string; title: string; placement: string; days: AdReportDay[] })[];
  totals: Counts;
  pipeline?: AdPipelineReport;
};
type Filters = { from: string; to: string; campaignId: string };
const DAY = 86400000;
const copy = {
  pipeline: text('Delivery stages', '投放阶段', '投放階段', 'Etapas de entrega'),
  pipelineNote: text('Delivery counts are aggregated asynchronously and can include repeat deliveries. They differ from the daily account counts above. Operator previews and registered QA are excluded.', '下发次数异步汇总，可能包含重复下发，与上方按日去重的账号数口径不同。运营预览和已登记 QA 不计入。', '下發次數非同步彙總，可能包含重複下發，與上方按日去重的帳號數口徑不同。營運預覽和已登記 QA 不計入。', 'Las entregas se agregan de forma asíncrona y pueden repetirse. Difieren de las cuentas únicas diarias. Se excluyen vistas previas de operadores y QA registrado.'),
  served: text('Served', '已下发', '已下發', 'Enviado'),
  rendered: text('Rendered', '已渲染', '已繪製', 'Renderizado'),
  viewability_candidate: text('Visibility candidate', '可见候选', '可見候選', 'Candidato visible'),
  valid_viewable: text('Viewable delivery', '有效展示下发', '有效展示下發', 'Entrega visible'),
  click: text('Delivery clicks', '下发点击', '下發點擊', 'Clics por entrega'),
  title: text('Advertising reports', '广告报表', '廣告報表', 'Informes publicitarios'),
  from: text(
    'First day · UTC, inclusive',
    '起始日 · UTC，含当日',
    '起始日 · UTC，含當日',
    'Primer día · UTC, incluido',
  ),
  to: text(
    'Last day · UTC, inclusive',
    '结束日 · UTC，含当日',
    '結束日 · UTC，含當日',
    'Último día · UTC, incluido',
  ),
  campaign: text('Campaign ID · optional', '活动 ID · 选填', '活動 ID · 選填', 'ID de campaña · opcional'),
  all: text(
    'Leave blank for all campaigns',
    '留空查询全部活动',
    '留空查詢全部活動',
    'Vacío para todas las campañas',
  ),
  load: text('Load report', '加载报表', '載入報表', 'Cargar informe'),
  loading: text('Loading the report…', '正在加载报表…', '正在載入報表…', 'Cargando el informe…'),
  idle: text(
    'Choose a UTC period, then load the report. No report has been requested yet.',
    '选择 UTC 日期范围后加载报表，目前尚未请求报表。',
    '選擇 UTC 日期範圍後載入報表，目前尚未請求報表。',
    'Elige un periodo UTC y carga el informe. Todavía no se ha solicitado.',
  ),
  dateNote: text(
    'Both dates are included. Default: the last 31 UTC days through today. Maximum: 366 days.',
    '起止日期均包含当日。默认截至 UTC 今日的最近 31 天，最多查询 366 天。',
    '起訖日期均包含當日。預設截至 UTC 今日的最近 31 天，最多查詢 366 天。',
    'Se incluyen ambos días. Por defecto: los últimos 31 días UTC hasta hoy. Máximo: 366 días.',
  ),
  measurement: text(
    'Internal estimates from daily-deduplicated account reports, not independently certified impressions or counts of real people. Registered QA accounts are excluded, including their earlier records. A click does not create an impression.',
    '内部估计按账号、活动和 UTC 日去重，非独立认证曝光，也不代表真实人数。已排除登记 QA 账号，包括其早先记录。点击不会自动增加曝光。',
    '內部估計按帳號、活動和 UTC 日去重，非獨立認證曝光，也不代表真實人數。已排除登記 QA 帳號，包括其較早紀錄。點擊不會自動增加曝光。',
    'Estimaciones internas de avisos de cuentas únicos por campaña y día UTC; no son impresiones certificadas ni personas verificadas. Se excluyen las cuentas QA registradas, incluidos sus registros anteriores. Un clic no crea una impresión.',
  ),
  uniqueNote: text(
    'Period reach counts accounts with an impression; clickers count accounts with a click. Overall unique counts deduplicate across dates and campaigns, so campaign uniques must not be added together. Impression/click totals add the daily counts.',
    '期间触达统计有曝光的账号，点击账号统计有点击的账号。总独立账号数跨日期、跨活动去重，不能把各活动独立数相加。曝光和点击合计是逐日值的累加。',
    '期間觸及統計有曝光的帳號，點擊帳號統計有點擊的帳號。總獨立帳號數跨日期、跨活動去重，不能把各活動獨立數相加。曝光和點擊合計是逐日值的累加。',
    'El alcance cuenta cuentas con impresión; los clicadores, cuentas con clic. Los únicos totales eliminan duplicados entre fechas y campañas: no sumes los únicos de cada campaña. Impresiones y clics suman los valores diarios.',
  ),
  period: text('Loaded period · UTC', '已加载区间 · UTC', '已載入區間 · UTC', 'Periodo cargado · UTC'),
  days: text('days', '天', '天', 'días'),
  impressions: text(
    'Impression reports · daily totals',
    '曝光上报 · 按日累计',
    '曝光上報 · 按日累計',
    'Avisos de impresión · suma diaria',
  ),
  clicks: text(
    'Click reports · daily totals',
    '点击上报 · 按日累计',
    '點擊上報 · 按日累計',
    'Avisos de clic · suma diaria',
  ),
  uniqueReach: text(
    'Distinct reached accounts · period',
    '独立触达账号 · 期间',
    '獨立觸及帳號 · 期間',
    'Cuentas alcanzadas únicas · periodo',
  ),
  uniqueClickers: text(
    'Distinct clicking accounts · period',
    '独立点击账号 · 期间',
    '獨立點擊帳號 · 期間',
    'Cuentas con clic únicas · periodo',
  ),
  empty: text(
    'No campaigns match this period and filter.',
    '该区间和筛选下没有活动。',
    '此區間與篩選條件下沒有活動。',
    'No hay campañas para este periodo y filtro.',
  ),
  zero: text(
    'No recorded impressions or clicks in this period.',
    '该区间没有曝光或点击记录。',
    '此區間沒有曝光或點擊紀錄。',
    'No hay impresiones ni clics registrados en este periodo.',
  ),
  daily: text('Show daily counts', '查看逐日数据', '查看逐日資料', 'Ver cifras diarias'),
  collapse: text('Hide daily counts', '收起逐日数据', '收起逐日資料', 'Ocultar cifras diarias'),
  day: text('Day · UTC', '日期 · UTC', '日期 · UTC', 'Día · UTC'),
  dayImpressions: text('Impressions', '曝光', '曝光', 'Impresiones'),
  dayClicks: text('Clicks', '点击', '點擊', 'Clics'),
  campaigns: text('Campaigns', '活动', '活動', 'Campañas'),
  previous: text('Previous', '上一页', '上一頁', 'Anterior'),
  next: text('Next', '下一页', '下一頁', 'Siguiente'),
  download: text('Download aggregate CSV', '下载聚合 CSV', '下載彙總 CSV', 'Descargar CSV agregado'),
  downloading: text('Preparing CSV…', '正在准备 CSV…', '正在準備 CSV…', 'Preparando CSV…'),
  downloaded: text(
    'CSV prepared for download.',
    'CSV 已准备下载。',
    'CSV 已準備下載。',
    'CSV preparado para descargar.',
  ),
  csvNote: text(
    'CSV uses the last successfully loaded filters and includes all matching campaigns and days, regardless of this page’s pagination.',
    'CSV 使用上次成功加载的筛选，包含全部匹配活动和日期，不受当前分页影响。',
    'CSV 使用上次成功載入的篩選，包含全部符合條件的活動和日期，不受目前分頁影響。',
    'El CSV usa los últimos filtros cargados con éxito e incluye todas las campañas y fechas, sin limitarse a esta página.',
  ),
  changed: text(
    'Filters changed. Load again to update the report and CSV.',
    '筛选已修改，请重新加载以更新报表和 CSV。',
    '篩選已修改，請重新載入以更新報表與 CSV。',
    'Los filtros cambiaron. Carga de nuevo para actualizar el informe y el CSV.',
  ),
  invalidDates: text(
    'Enter real UTC dates in order, covering at most 366 days.',
    '请按先后顺序填写有效 UTC 日期，范围最多 366 天。',
    '請依先後順序填寫有效 UTC 日期，範圍最多 366 天。',
    'Introduce fechas UTC válidas y ordenadas, con un máximo de 366 días.',
  ),
  invalidCampaign: text(
    'Enter a valid campaign UUID or leave it blank.',
    '请填写有效活动 UUID，或留空。',
    '請填寫有效活動 UUID，或留空。',
    'Introduce un UUID de campaña válido o déjalo vacío.',
  ),
  invalidFilters: text(
    'Check the UTC dates (at most 366 days) and the optional campaign UUID.',
    '请检查 UTC 起止日期（最多 366 天）及选填的活动 UUID。',
    '請檢查 UTC 起訖日期（最多 366 天）及選填的活動 UUID。',
    'Revisa las fechas UTC (máximo 366 días) y el UUID de campaña opcional.',
  ),
  missing: text(
    'Campaign not found. Check its ID or clear the filter.',
    '未找到此活动，请检查 ID 或清空筛选。',
    '找不到此活動，請檢查 ID 或清空篩選。',
    'No se encontró la campaña. Revisa el ID o quita el filtro.',
  ),
};
export function defaultAdReportFilters(nowMs = Date.now()): Filters {
  const to = new Date(nowMs).toISOString().slice(0, 10);
  return {
    from: new Date(Date.parse(`${to}T00:00:00Z`) - 30 * DAY).toISOString().slice(0, 10),
    to,
    campaignId: '',
  };
}
export function adReportQuery(filters: Filters): string {
  const date = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
    const parsed = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value ? parsed : NaN;
  };
  const from = date(filters.from),
    to = date(filters.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from > to || (to - from) / DAY + 1 > 366)
    throw new Error('AD_REPORT_DATES');
  const campaignId = filters.campaignId.trim();
  if (campaignId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(campaignId))
    throw new Error('AD_REPORT_CAMPAIGN');
  const query = new URLSearchParams({ from: filters.from, to: filters.to });
  if (campaignId) query.set('campaignId', campaignId);
  return query.toString();
}
function reportError(locale: Locale, error: unknown) {
  if (error instanceof Error && error.message === 'AD_REPORT_DATES') return copy.invalidDates[locale];
  if (error instanceof Error && error.message === 'AD_REPORT_CAMPAIGN') return copy.invalidCampaign[locale];
  if (error instanceof ApiError && error.code === 'AD_REPORT_RANGE') return copy.invalidFilters[locale];
  if (error instanceof ApiError && error.status === 404) return copy.missing[locale];
  if (error instanceof ApiError && error.status === 400) return copy.invalidDates[locale];
  return errorText(locale, error);
}
function CountsGrid({ counts, locale }: { counts: Counts; locale: Locale }) {
  return (
    <div className="admin-stats">
      {(['impressions', 'clicks', 'uniqueReach', 'uniqueClickers'] as const).map((key) => (
        <div key={key}>
          <b>{new Intl.NumberFormat(locale).format(counts[key])}</b>
          <small>{copy[key][locale]}</small>
        </div>
      ))}
    </div>
  );
}
function PipelineCounts({ counts, locale }: { counts?: AdStageCounts; locale: Locale }) {
  if (!counts) return null;
  return <div className="admin-stats">{(['served','rendered','viewability_candidate','valid_viewable','click'] as const).map(key => <div key={key}><b>{new Intl.NumberFormat(locale).format(counts[key])}</b><small>{copy[key][locale]}</small></div>)}</div>;
}
function DayTable({ days, locale }: { days: AdReportDay[]; locale: Locale }) {
  const t = (key: keyof typeof copy) => copy[key][locale];
  return (
    <div style={{ overflow: 'auto', maxHeight: 360 }} tabIndex={0} role="region" aria-label={t('daily')}>
      <table
        style={{ width: '100%', minWidth: 340, borderCollapse: 'collapse', textAlign: 'left', fontSize: 12 }}
      >
        <thead>
          <tr>
            <th scope="col">{t('day')}</th>
            <th scope="col">{t('dayImpressions')}</th>
            <th scope="col">{t('dayClicks')}</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day.day}>
              <th scope="row" style={{ fontWeight: 400, padding: '8px 4px' }}>
                {day.day}
              </th>
              <td>{new Intl.NumberFormat(locale).format(day.impressions)}</td>
              <td>{new Intl.NumberFormat(locale).format(day.clicks)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function AdReportResult({ report, locale }: { report: AdReport; locale: Locale }) {
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string>();
  const t = (key: keyof typeof copy) => copy[key][locale];
  const pages = Math.max(1, Math.ceil(report.campaigns.length / 20));
  const currentPage = Math.min(page, pages - 1);
  return (
    <section aria-label={t('title')}>
      <p>
        <strong>
          {t('period')}: {report.from} — {report.to} · {report.dayCount} {t('days')}
        </strong>
      </p>
      <CountsGrid counts={report.totals} locale={locale} />
      {report.pipeline && <><h3>{t('pipeline')}</h3><p className="muted small">{t('pipelineNote')}</p><PipelineCounts counts={report.pipeline.totals} locale={locale}/></>}
      {!report.campaigns.length ? (
        <p role="status">{t('empty')}</p>
      ) : (
        <>
          <h3>
            {t('campaigns')} · {report.campaigns.length}
          </h3>
          {report.campaigns.slice(currentPage * 20, currentPage * 20 + 20).map((campaign) => (
            <article className="admin-report" key={campaign.campaignId}>
              <strong>{campaign.title}</strong>
              <small style={{ overflowWrap: 'anywhere' }}>
                {campaign.placement} · {campaign.campaignId}
              </small>
              <CountsGrid counts={campaign} locale={locale} />
              <PipelineCounts counts={report.pipeline?.campaigns.find(row => row.campaignId === campaign.campaignId)?.counts} locale={locale}/>
              {!campaign.impressions && !campaign.clicks && <p>{t('zero')}</p>}
              <button
                className="button soft compact"
                aria-expanded={expanded === campaign.campaignId}
                onClick={() =>
                  setExpanded(expanded === campaign.campaignId ? undefined : campaign.campaignId)
                }
              >
                {t(expanded === campaign.campaignId ? 'collapse' : 'daily')}
              </button>
              {expanded === campaign.campaignId && <DayTable days={campaign.days} locale={locale} />}
            </article>
          ))}
          <div className="friend-actions">
            <button
              className="button soft compact"
              disabled={currentPage === 0}
              onClick={() => {
                setPage(currentPage - 1);
                setExpanded(undefined);
              }}
            >
              {t('previous')}
            </button>
            <span>
              {currentPage + 1} / {pages}
            </span>
            <button
              className="button soft compact"
              disabled={currentPage + 1 >= pages}
              onClick={() => {
                setPage(currentPage + 1);
                setExpanded(undefined);
              }}
            >
              {t('next')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export default function AdReportPanel({ locale }: { locale: Locale }) {
  const t = (key: keyof typeof copy) => copy[key][locale];
  const [filters, setFilters] = useState<Filters>(defaultAdReportFilters);
  const [report, setReport] = useState<AdReport>();
  const [loadedQuery, setLoadedQuery] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const request = useRef<AbortController>(undefined);
  useEffect(() => () => request.current?.abort(), []);
  let changed = false;
  if (loadedQuery) {
    try {
      changed = adReportQuery(filters) !== loadedQuery;
    } catch {
      changed = true;
    }
  }
  const download = async () => {
    if (!report || !loadedQuery || busy || csvBusy) return;
    setCsvBusy(true);
    setError('');
    setMessage('');
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = await fetch(`/api/ads/reports.csv?${loadedQuery}`, {
        credentials: 'include',
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(30000)]),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApiError(body.code || 'REQUEST', body.message || 'Report unavailable.', response.status);
      }
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tavmix-ad-report-${report.from}-${report.to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setMessage(t('downloaded'));
    } catch (e) {
      if (!controller.signal.aborted) setError(reportError(locale, e));
    } finally {
      if (!controller.signal.aborted) setCsvBusy(false);
    }
  };
  return (
    <div>
      <h3>{t('title')}</h3>
      <p className="muted small">{t('measurement')}</p>
      <p className="muted small">{t('uniqueNote')}</p>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy || csvBusy) return;
          setError('');
          setMessage('');
          let query: string;
          try {
            query = adReportQuery(filters);
          } catch (e) {
            setError(reportError(locale, e));
            return;
          }
          const controller = new AbortController();
          request.current = controller;
          setBusy(true);
          setReport(undefined);
          setLoadedQuery(undefined);
          try {
            const data = await api<AdReport>(`/ads/reports?${query}`, {
              signal: AbortSignal.any([controller.signal, AbortSignal.timeout(30000)]),
            });
            if (!controller.signal.aborted) {
              setReport(data);
              setLoadedQuery(query);
            }
          } catch (e) {
            if (!controller.signal.aborted) setError(reportError(locale, e));
          } finally {
            if (!controller.signal.aborted) setBusy(false);
          }
        }}
      >
        <label>
          {t('from')}
          <input
            type="date"
            required
            value={filters.from}
            disabled={busy || csvBusy}
            onChange={(event) => setFilters({ ...filters, from: event.target.value })}
          />
        </label>
        <label>
          {t('to')}
          <input
            type="date"
            required
            value={filters.to}
            disabled={busy || csvBusy}
            onChange={(event) => setFilters({ ...filters, to: event.target.value })}
          />
        </label>
        <p className="muted tiny">{t('dateNote')}</p>
        <label>
          {t('campaign')}
          <input
            value={filters.campaignId}
            placeholder={t('all')}
            maxLength={36}
            autoComplete="off"
            disabled={busy || csvBusy}
            onChange={(event) => setFilters({ ...filters, campaignId: event.target.value })}
          />
        </label>
        <button className="button primary full" disabled={busy || csvBusy}>
          <RefreshCw size={14} />
          {busy ? t('loading') : t('load')}
        </button>
      </form>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {busy ? (
        <p role="status">{t('loading')}</p>
      ) : !report && !error ? (
        <p className="muted" role="status">
          {t('idle')}
        </p>
      ) : null}
      {report && (
        <>
          <AdReportResult key={loadedQuery} report={report} locale={locale} />
          {changed && <p role="status">{t('changed')}</p>}
          <p className="muted tiny">{t('csvNote')}</p>
          <button className="button soft full" disabled={busy || csvBusy} onClick={download}>
            <Download size={14} />
            {csvBusy ? t('downloading') : t('download')}
          </button>
        </>
      )}
      {message && (
        <p className="green" role="status">
          {message}
        </p>
      )}
    </div>
  );
}
